import React from 'react';
import { Shield, Cpu, Wrench, Car, Snowflake, Phone, MapPin, Clock, CheckCircle, Award } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const WarrantyShowcase = () => {
  const { symbol, t, selectedCountry } = useCurrency();
  const isIndia = selectedCountry.code === 'IN';

  const coverageItems = [
    {
      icon: Cpu,
      title: t('warrantyShowcase.coverage.engine.title'),
      desc: t('warrantyShowcase.coverage.engine.desc'),
      highlight: t('warrantyShowcase.coverage.engine.highlight'),
    },
    {
      icon: Wrench,
      title: t('warrantyShowcase.coverage.electrical.title'),
      desc: t('warrantyShowcase.coverage.electrical.desc'),
      highlight: t('warrantyShowcase.coverage.electrical.highlight'),
    },
    {
      icon: Car,
      title: t('warrantyShowcase.coverage.mechanical.title'),
      desc: t('warrantyShowcase.coverage.mechanical.desc'),
      highlight: t('warrantyShowcase.coverage.mechanical.highlight'),
    },
    {
      icon: Snowflake,
      title: t('warrantyShowcase.coverage.cooling.title'),
      desc: t('warrantyShowcase.coverage.cooling.desc'),
      highlight: t('warrantyShowcase.coverage.cooling.highlight'),
    },
    {
      icon: Phone,
      title: t('warrantyShowcase.coverage.roadside.title'),
      desc: t('warrantyShowcase.coverage.roadside.desc'),
      highlight: t('warrantyShowcase.coverage.roadside.highlight'),
    },
    {
      icon: MapPin,
      title: isIndia ? t('warrantyShowcase.coverage.network.titleIndia') : t('warrantyShowcase.coverage.network.titleMY'),
      desc: isIndia ? t('warrantyShowcase.coverage.network.descIndia') : t('warrantyShowcase.coverage.network.descMY'),
      highlight: isIndia ? t('warrantyShowcase.coverage.network.highlightIndia') : t('warrantyShowcase.coverage.network.highlightMY'),
    },
  ];

  const stats = [
    { value: '98%', label: t('warrantyShowcase.stats.claimApproval') },
    { value: '24hrs', label: t('warrantyShowcase.stats.claimProcessing') },
    { value: isIndia ? '7000+' : '500+', label: t('warrantyShowcase.stats.serviceCenters') },
    { value: `${symbol}0`, label: t('warrantyShowcase.stats.deductible') },
  ];

  return (
    <section className="py-14 bg-gradient-to-b from-emerald-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
            <Shield className="h-4 w-4" />
            {t('warrantyShowcase.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {t('warrantyShowcase.title')}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {isIndia ? t('warrantyShowcase.subtitle') : t('warrantyShowcase.subtitleMY')}
          </p>
        </div>

        {/* Stats Bar */}
        <div className="bg-slate-900 rounded-2xl p-5 mb-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Coverage Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {coverageItems.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-5 border border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                  <item.icon className="h-6 w-6 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-slate-900">{item.title}</h3>
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {item.highlight}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Banner */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Award className="h-6 w-6 text-emerald-200" />
                <span className="text-emerald-100 font-medium">{t('warrantyShowcase.banner.badge')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {t('warrantyShowcase.banner.title')}
              </h3>
              <p className="text-emerald-100">
                {t('warrantyShowcase.banner.desc')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{t('warrantyShowcase.banner.duration')}</div>
                <div className="text-emerald-100 text-sm">{t('warrantyShowcase.banner.durationLabel')}</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{t('warrantyShowcase.banner.payout')}</div>
                <div className="text-emerald-100 text-sm">{t('warrantyShowcase.banner.payoutLabel')}</div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-6 pt-6 border-t border-emerald-500/30 flex flex-wrap items-center justify-center gap-6">
            {['Maruti', 'Hyundai', 'Honda', 'Toyota', 'Tata', 'Mahindra', 'Kia', 'MG'].map((brand) => (
              <span key={brand} className="text-emerald-200 text-sm font-medium">
                {brand} {t('warrantyShowcase.banner.authorized')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WarrantyShowcase;

